<template>
  <div class="col-12 md:col-8 lg:col-6 mb-0 mb-0 mt-5 float-right order-3 md:order-2 relative">
    <h3 class="h5 font-medium font-family-base mb-2">
      {{ t('quantity_discount') }}
    </h3>
    <div class="bg-white inline-block absolute right-0 top-0 rounded-pill px-3 py-2 mt-n2">
      {{ t('you_save') }} <strong class="text-success">{{ totalQuantityDiscount }} {{ product.currency }}</strong>
    </div>
    <ul class="list-unstyled mb-0 row mx-0 justify-between mt-4">
      <li
        v-for="discount in product.quantityDiscounts"
        :key="discount.id"
        :class="[
          {
            'text-primary shadow discount-milestone-active':
              currentDiscountItem &&
              discount.quantityFrom === currentDiscountItem.quantityFrom &&
              discount.quantityTo === currentDiscountItem.quantityTo,
          },
          'xl:px-3 py-2 px-1 rounded-lg mb-3 relative text-center discount-milestone md:col-auto col-3',
        ]"
      >
        {{ discount.label ? discount.label : discountLabel(discount) }}<br>
        <strong>{{ numberToCurrency(discount.price) }},- / {{ t('piece') }}</strong>
      </li>
    </ul>
    <div class="discount-progress-bar rounded-pill overflow-hidden w-100 relative">
      <div
        class="discount-progress-line absolute left-0 position h-100 bg-primary"
        :style="{ width: `${progressPercent}%` }"
      />
    </div>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { numberToCurrency } from '@/mixins/numberToCurrency'
import { useCartStore } from '@/stores/CartStore'

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
})

const maximumItems = props.product.quantityDiscounts.at(-1).quantityFrom

const { t } = useI18n()
const cartStore = useCartStore()
const { getCart } = storeToRefs(cartStore)

const productInCart = computed(
  () => getCart.value.cartProducts?.find((i) => i.productID === props.product.id) || undefined
)
const quantityInCart = computed(() => productInCart.value?.quantity || 0)
const currentDiscountItem = computed(() => {
  return props.product.quantityDiscounts.find((di) => {
    return di.quantityFrom <= quantityInCart.value && di.quantityTo >= quantityInCart.value
  })
})
const totalQuantityDiscount = computed(() => {
  if (quantityInCart.value > 0 && currentDiscountItem.value) {
    return (
      Math.round(
        (props.product.price - Number.parseFloat(currentDiscountItem.value.price)) * quantityInCart.value * 100
      ) / 100
    )
  }
  return 0
})

const progressPercent = computed(() => {
  if (quantityInCart.value / (maximumItems / 100) >= 100) {
    return 100
  }
  return quantityInCart.value / (maximumItems / 100)
})

const discountLabel = (discount) => {
  let label = ''
  if (discount.quantityFrom === 1) {
    label = t('buy_one_piece')
  } else if (discount.quantityFrom > 1 && discount.quantityTo !== discount.quantityFrom) {
    label = t('buy_more_pieces', {
      from: discount.quantityFrom,
      to: discount.quantityTo,
    })
  }
  return label
}
</script>

<style lang="scss" scoped>
.discount-progress-bar {
  height: 14px;
  background-color: rgba(79, 43, 27, 0.4);

  .discount-progress-line {
    transition: 0.5s width;
    max-width: 100%;
  }
}
.discount-milestone {
  transition: 0.5s background-color;
  background-color: rgba(0, 0, 0, 0);
  &:before {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    border-left: 8px solid rgba(0, 0, 0, 0);
    border-right: 8px solid rgba(0, 0, 0, 0);
    border-top: 8px solid rgba(0, 0, 0, 0);
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    transition: 0.5s border-top;
  }
  &.discount-milestone-active {
    background-color: $white;
    &:before {
      border-top: 8px solid #ffffff;
    }
  }

  @media screen and (max-width: 991px) {
    &.discount-milestone-active {
      background-color: $primary-color-450;
      &:before {
        border-top: 8px solid #f2b76f;
      }
    }
  }
}
</style>

<i18n>
  {
    "cs": {
    "buy_more_pieces": "Při koupi %{from} až %{to} ks",
    "buy_one_piece": "Při koupi 1 ks",
    "piece": "ks",
    "quantity_discount": "Množstevní slevy",
    "you_save": "Ušetříte"
},
    "sk": {
    "buy_more_pieces": "Pri kúpe %{from} až %{to} ks",
    "buy_one_piece": "Pri kúpe 1 ks",
    "piece": "ks",
    "quantity_discount": "Množstevné zľavy",
    "you_save": "Ušetríte"
},
    "pl": {
    "buy_more_pieces": "Při koupi %{from} a %{to} ks",
    "buy_one_piece": "1 szt.",
    "piece": "szt.",
    "quantity_discount": "Rabaty ilościowe",
    "you_save": "Oszczędzasz"
},
    "hu": {
    "buy_more_pieces": "Při koupi %{from} a %{to} ks",
    "buy_one_piece": "1 db vásárlása esetén",
    "piece": "Db",
    "quantity_discount": "Mennyiségi kedvezmények",
    "you_save": "Megtakarítasz"
},
    "de": {
    "buy_more_pieces": "Beim Kauf von %{from} bis %{to} Stück",
    "buy_one_piece": "Beim Kauf von 1 Stk.",
    "piece": "Stk.",
    "quantity_discount": "Mengenrabatt",
    "you_save": "Sie sparen"
},
    "at": {
    "buy_more_pieces": "Beim Kauf von %{from} bis %{to} Stück",
    "buy_one_piece": "Beim Kauf von 1 Stk.",
    "piece": "Stk.",
    "quantity_discount": "Mengenrabatt",
    "you_save": "Sie sparen"
},
    "ro": {
    "buy_more_pieces": "Při koupi %{from} a %{to} ks",
    "buy_one_piece": "Dacă comanzi: 1 buc.",
    "piece": "Bc",
    "quantity_discount": "Reduceri cantitative",
    "you_save": "Economisești"
}
  }
</i18n>
